/*
 * Cold start. Core builds the bootstrap envelope and hands each module its own opaque entry of
 * `payload.modules` (protocol.md §2.7, S0-PLAN R-O); this is chat's: the sidebar with one read
 * state per conversation. The draft summaries the payload also carries are not read here: the
 * device's own draft store (core/drafts) is what the composer and the sidebar read, and a second
 * copy of them in the model would be a mirror nobody looks in.
 *
 * The payload is JSON from the wire, so it is validated on shape before it becomes model state: a
 * server that sends nothing for chat (the module disabled, an older build) must leave a working,
 * empty client rather than a crashed one.
 */

import type { StoreApi } from "zustand"
import type { ConversationSlice, Model } from "../../../core/store/model.ts"
import type { BootstrapConversation, ChatBootstrap } from "../wire/views.ts"
import { chatSlice, withChatSlice } from "./selectors.ts"
import { emptyChatSlice } from "./slice.ts"

function isChatBootstrap(payload: unknown): payload is ChatBootstrap {
	if (typeof payload !== "object" || payload === null) return false
	const candidate = payload as Partial<ChatBootstrap>
	return Array.isArray(candidate.conversations)
}

/**
 * The order the server sent — starred first, then the most recent message — which is what
 * `conversationOrder` carries into the model. The **sidebar** no longer reads it: its order is a
 * selector recomputed on every membership event (`selectors.ts` `sidebarOrder`), because a sort done
 * once at cold start could not answer a star set five minutes later (X-A4 N18.4). It is still the
 * order every other reader of `conversationOrder` gets — the search filter bar, the setup checklist —
 * and the tiebreak the selector falls back on, so it stays.
 */
function sortEntries(entries: BootstrapConversation[]): BootstrapConversation[] {
	return entries.slice().sort((a, b) => {
		if (a.self.starred !== b.self.starred) return a.self.starred ? -1 : 1
		return (b.conversation.lastMessageAt ?? 0) - (a.conversation.lastMessageAt ?? 0)
	})
}

export function hydrateChat(model: Model, payload: unknown): Model {
	if (!isChatBootstrap(payload)) return model

	const slice = emptyChatSlice()
	// The visit's unread anchors survive a bootstrap. A reconnect replaces everything the *server*
	// knows, and the seq a reader's "New messages" line was drawn from is not one of those things:
	// it is where they stopped reading a minute ago, and it is pinned again only when they arrive
	// (X-B3 A3). Dropped here, the line leaves a conversation somebody is looking at.
	slice.unreadAnchor = chatSlice(model).unreadAnchor
	const conversations: Record<number, ConversationSlice> = {}
	const order: number[] = []

	for (const entry of sortEntries(payload.conversations)) {
		const { conversation, self } = entry
		conversations[conversation.id] = {
			meta: { ...conversation, memberIds: conversation.memberIds ?? [] },
			status: "cold",
			lastSeq: conversation.lastSeq,
			unread: entry.unread,
			mentions: entry.mentions,
		}
		order.push(conversation.id)
		slice.self[conversation.id] = self
		slice.cursors[conversation.id] = {
			conv: conversation.id,
			lastReadSeq: self.lastReadSeq,
			version: self.readVersion,
			manualUnread: self.manualUnread,
			unread: entry.unread,
			mentions: entry.mentions,
		}
	}
	return withChatSlice({ ...model, conversations, conversationOrder: order, messages: {} }, slice)
}

/** `FrontendModule.onBootstrap`: write the payload into the store the shell handed over. */
export function onChatBootstrap(payload: unknown, store: StoreApi<Model>): void {
	store.setState((model) => hydrateChat(model, payload))
}
